import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { Search } from "lucide-react";
import { CloudIcon } from "@/components/brand-icons";

export function ProjectSearch() {
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    client.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return projects.filter((p) => p.name.toLowerCase().includes(s)).slice(0, 8);
  }, [q, projects]);

  const go = (p) => {
    if (!p) return;
    setQ("");
    setOpen(false);
    nav(`/app/projects/${p.id}`);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); go(results[idx]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={ref} className="relative w-72" onBlur={(e) => { if (!ref.current?.contains(e.relatedTarget)) setOpen(false); }}>
      <div className="flex h-9 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 focus-within:ring-2 focus-within:ring-slate-900/10">
        <Search className="h-3.5 w-3.5 text-slate-400" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setIdx(0); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Jump to a project…"
          className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
          data-testid="project-search-input"
        />
      </div>
      {open && q.trim() && (
        <div className="absolute right-0 z-30 mt-1 w-full overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg" data-testid="project-search-results">
          {results.map((p, i) => (
            <button
              key={p.id}
              type="button"
              tabIndex={-1}
              onMouseDown={(e) => { e.preventDefault(); go(p); }}
              onMouseEnter={() => setIdx(i)}
              className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm ${i === idx ? "bg-slate-900 text-white" : "text-slate-700"}`}
              data-testid={`project-search-result-${p.name}`}
            >
              <span className="truncate font-medium">{p.name}</span>
              <span className="ml-3 inline-flex items-center gap-1.5 font-mono-tight text-[10px] uppercase tracking-widest opacity-70">
                <CloudIcon cloud={p.cloud} className="h-3 w-3" /> {p.cloud}
              </span>
            </button>
          ))}
          {!results.length && (
            <p className="px-3 py-3 text-sm text-slate-500" data-testid="project-search-empty">No project matches "{q.trim()}".</p>
          )}
        </div>
      )}
    </div>
  );
}
